import React from 'react';
import { useKeyboard } from '../../../context/KeyboardContext';

// Base styles for keys (slightly more compact than before)
const baseKeyStyle = {
  fontSize: 24,
  borderRadius: 12,
  border: '1px solid #ddd',
  background: '#fafafa',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  touchAction: 'manipulation',
  WebkitTapHighlightColor: 'transparent',
  userSelect: 'none',
  cursor: 'pointer',
  outline: 'none'
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 8,
  padding: 8
};

const keySquareStyle = {
  ...baseKeyStyle,
  // Match alpha keyboard key height (compact, rectangular keys)
  height: 44,
  width: '100%',
  transition: 'background 80ms ease, box-shadow 80ms ease'
};

const actionRowStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
  padding: '0 12px 12px 12px'
};

const actionKeyStyle = {
  ...baseKeyStyle,
  height: 48,
  fontSize: 18,
  transition: 'background 80ms ease, box-shadow 80ms ease'
};

const NumericKeypad = () => {
  const kb = useKeyboard();
  const { allowDecimal } = kb?.options || {};
  const showDone = kb?.options?.showEnter !== false; // default true; hide when explicitly false
  const [pressedKey, setPressedKey] = React.useState(null);

  const release = () => setPressedKey(null);

  const handlePress = (val, id) => (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setPressedKey(id);
    kb.onInput && kb.onInput(val);
  };

  const handleBackspace = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setPressedKey('backspace');
    kb.onBackspace && kb.onBackspace();
  };

  const handleDone = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setPressedKey('done');
    kb.onEnter && kb.onEnter();
  };

  const handleClose = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setPressedKey('close');
    kb.close && kb.close();
  };

  // Grid keys in 3 columns:
  // 1 2 3
  // 4 5 6
  // 7 8 9
  // 0 ⌫ ↵
  const keys = [
    '1','2','3',
    '4','5','6',
    '7','8','9',
    '0','⌫','↵'
  ];

  return (
    <div>
      <div style={gridStyle}>
        {keys.map((k, idx) => {
          if (k === null) {
            return <div key={`empty-${idx}`} />;
          }
          if (k === '⌫') {
            return (
              <button
                key={'backspace'}
                type="button"
                style={{
                  ...keySquareStyle,
                  background: pressedKey === 'backspace' ? '#fecaca' : '#ffe8e8',
                  boxShadow: pressedKey === 'backspace' ? 'inset 0 2px 6px rgba(0,0,0,0.12)' : undefined
                }}
                onPointerDown={handleBackspace}
                onPointerUp={release}
                onPointerLeave={release}
                onPointerCancel={release}
              >
                ⌫
              </button>
            );
          }
          if (k === '↵') {
            return (
              <button
                key={'enter'}
                type="button"
                style={{
                  ...keySquareStyle,
                  background: pressedKey === 'enter' ? '#bbdefb' : '#e3f2fd',
                  boxShadow: pressedKey === 'enter' ? 'inset 0 2px 6px rgba(0,0,0,0.12)' : undefined
                }}
                onPointerDown={(e) => { if (e && e.preventDefault) e.preventDefault(); setPressedKey('enter'); kb.onEnter && kb.onEnter(); }}
                onPointerUp={release}
                onPointerLeave={release}
                onPointerCancel={release}
                aria-label="Enter"
                title="Enter"
              >
                ↵
              </button>
            );
          }
          return (
            <button
              key={k + idx}
              type="button"
              style={{
                ...keySquareStyle,
                background: pressedKey === `k-${idx}` ? '#e0e7ff' : '#fafafa',
                boxShadow: pressedKey === `k-${idx}` ? 'inset 0 2px 6px rgba(0,0,0,0.12)' : undefined
              }}
              onPointerDown={handlePress(String(k), `k-${idx}`)}
              onPointerUp={release}
              onPointerLeave={release}
              onPointerCancel={release}
            >
              {k}
            </button>
          );
        })}
      </div>
      <div style={{
        ...actionRowStyle,
        gridTemplateColumns: showDone ? '1fr 1fr' : '1fr',
        padding: '0 12px 8px 12px'
      }}>
        {showDone && (
          <button
            type="button"
            style={{
              ...actionKeyStyle,
              background: pressedKey === 'done' ? '#bbdefb' : '#e3f2fd',
              boxShadow: pressedKey === 'done' ? 'inset 0 2px 6px rgba(0,0,0,0.12)' : undefined
            }}
            onPointerDown={handleDone}
            onPointerUp={release}
            onPointerLeave={release}
            onPointerCancel={release}
          >
            Done
          </button>
        )}
        <button
          type="button"
          aria-label="Close Keyboard"
          style={{
            ...actionKeyStyle,
            fontWeight: 600,
            border: '1px solid #d1c4e9',
            color: '#4a148c',
            background: '#f3e5f5',
            borderRadius: 9999,
            boxShadow: pressedKey === 'close' ? 'inset 0 2px 6px rgba(0,0,0,0.12)' : '0 2px 6px rgba(0,0,0,0.08)'
          }}
          onPointerDown={handleClose}
          onPointerUp={release}
          onPointerLeave={release}
          onPointerCancel={release}
        >
          ✕ Close
        </button>
      </div>
    </div>
  );
};

export default NumericKeypad;
